import React from 'react';
import { Box, Chip, InputBase, Typography } from '@mui/material';

export interface TokenFieldProps {
  /** The entries made so far, e.g. email addresses. */
  value: string[];
  onChange: (next: string[]) => void;
  /** What an entry is, for the empty field: "Add emails". */
  placeholder?: string;
  /**
   * Tidies an entry as it is added ("Ann@X.com" → "ann@x.com"), or returns
   * null to refuse it; the refused text stays in the box, marked, with
   * `invalidText` under it.
   */
  normalise?: (raw: string) => string | null;
  invalidText?: string;
  disabled?: boolean;
  /**
   * What is typed and not yet a chip, as it changes. With it, a button beside
   * the box can act on the text as it stands, so nobody has to know that
   * Enter comes first.
   */
  onDraftChange?: (draft: string) => void;
  /**
   * Enter was pressed with everything in the box a good entry: the whole
   * list, typed text included. Enter then does the box's job (send, add)
   * and not only makes a chip; comma and space still just make chips.
   */
  onSubmit?: (entries: string[]) => void;
  /**
   * Turn what is typed into a chip by itself once it is a good entry and the
   * typing has paused, so the reader sees it has been taken. It waits for
   * the pause because "ann@x.co" is already a good email on the way to
   * "ann@x.com". Needs `normalise`, which is what says an entry is good.
   */
  addWhenValid?: boolean;
  /** Names the field for screen readers. */
  ariaLabel: string;
  id?: string;
}

/** How long the typing must pause before a good entry becomes a chip. */
const ADD_AFTER_MS = 800;

/**
 * Several short entries typed into one box, each becoming a chip that can be
 * removed: people to share with, by email. Enter, comma, space or leaving the
 * box adds what was typed; pasting a list adds each. Backspace in an empty box
 * turns the last chip back into text.
 *
 * For picking from known values, use `FilterSelect` or `OptionList`.
 */
export function TokenField({
  value, onChange, placeholder, normalise, invalidText = 'Check this one', disabled, ariaLabel, id,
  onDraftChange, onSubmit, addWhenValid = false,
}: TokenFieldProps) {
  const [draft, setDraftText] = React.useState('');
  const [invalid, setInvalid] = React.useState(false);
  const setDraft = (text: string) => { setDraftText(text); onDraftChange?.(text); };

  /** Adds what was typed; the list as it then stands, or null if any of it was refused. */
  const add = (raw: string): string[] | null => {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return value;
    const next = [...value];
    const refused: string[] = [];
    for (const part of parts) {
      const clean = normalise ? normalise(part) : part;
      if (clean === null) refused.push(part);
      else if (!next.includes(clean)) next.push(clean);
    }
    if (next.length !== value.length) onChange(next);
    setDraft(refused.join(' '));
    setInvalid(refused.length > 0);
    return refused.length === 0 ? next : null;
  };

  // A good entry, left alone for a moment, becomes a chip.
  const ready = addWhenValid && Boolean(normalise) && Boolean(draft.trim()) && normalise?.(draft.trim()) != null;
  const addRef = React.useRef(add);
  addRef.current = add;
  React.useEffect(() => {
    if (!ready) return undefined;
    const timer = window.setTimeout(() => addRef.current(draft), ADD_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [ready, draft]);

  const keyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ' ', ';'].includes(e.key)) {
      if (draft.trim()) e.preventDefault();
      const entries = draft.trim() ? add(draft) : value;
      if (e.key === 'Enter' && onSubmit && entries?.length) {
        e.preventDefault();
        onSubmit(entries);
      }
    } else if (e.key === 'Backspace' && !draft && value.length) {
      // The last chip comes back as text to carry on typing, so one made a
      // moment too soon ("ann@x.co") is finished, not retyped.
      e.preventDefault();
      onChange(value.slice(0, -1));
      setDraft(value[value.length - 1]);
    }
  };

  return (
    <Box>
      <Box
        sx={(t) => ({
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75,
          // The height of a medium control, so a button beside it
          // (`ToolbarButton size="medium"`) lines up edge to edge.
          minHeight: t.ds.sizes.control.md,
          px: 1, py: 0.25, boxSizing: 'border-box',
          borderRadius: `${t.ds.radius.md}px`,
          border: `1px solid ${invalid ? t.tokens.semantic.danger : t.tokens.border.default}`,
          bgcolor: t.tokens.surface.raised,
          '&:focus-within': { borderColor: t.tokens.border.focus, boxShadow: `0 0 0 3px ${t.tokens.brand.primarySoft}` },
        })}
      >
        {value.map((entry) => (
          <Chip
            key={entry}
            label={entry}
            size="small"
            disabled={disabled}
            onDelete={() => onChange(value.filter((v) => v !== entry))}
            sx={(t) => ({ ...t.typography.body2, bgcolor: t.tokens.surface.sunken, maxWidth: '100%' })}
          />
        ))}
        <InputBase
          id={id}
          value={draft}
          disabled={disabled}
          onChange={(e) => { setDraft(e.target.value); setInvalid(false); }}
          onKeyDown={keyDown}
          onBlur={() => add(draft)}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (/[\s,;]/.test(text)) { e.preventDefault(); add(`${draft} ${text}`); }
          }}
          placeholder={value.length ? '' : placeholder}
          inputProps={{ 'aria-label': ariaLabel, 'aria-invalid': invalid || undefined }}
          sx={(t) => ({ ...t.typography.body2, flex: 1, minWidth: 160, px: 0.5 })}
        />
      </Box>
      {invalid ? (
        <Typography variant="caption" role="alert" sx={(t) => ({ display: 'block', mt: 0.5, color: t.tokens.semantic.danger })}>
          {invalidText}
        </Typography>
      ) : null}
    </Box>
  );
}
